/** RTE-X11.5c-f — application picker scope for route create/edit Step 1. */

export type RoutePlanningApplicationRef = {
  id: number;
  internalNumber?: string | null;
  status?: string | null;
  client?: { name?: string | null } | null;
  consigneeId?: number | null;
  consignee?: { name?: string | null } | null;
  loadingAddress?: string | null;
  unloadingAddress?: string | null;
  routes?: Array<{
    route?: { id?: number; status?: string | null };
    id?: number;
    status?: string | null;
  }>;
};

const PLANNING_STATUSES = new Set(['CREATED', 'IN_WORK']);

const TERMINAL_ROUTE_STATUSES = new Set([
  'COMPLETED',
  'DONE',
  'FINISHED',
  'CANCELLED',
  'CANCELED'
]);

export function resolveApplicationActiveRouteId(
  app: RoutePlanningApplicationRef,
  excludeRouteId?: number
): number | undefined {
  const links = app.routes ?? [];
  for (const link of links) {
    const route = link.route ?? link;
    const routeId = Number(route.id);
    if (!Number.isFinite(routeId)) continue;
    if (excludeRouteId != null && routeId === excludeRouteId) continue;
    const status = String(route.status ?? '').trim().toUpperCase();
    if (!TERMINAL_ROUTE_STATUSES.has(status)) return routeId;
  }
  return undefined;
}

export function isApplicationAvailableForRoutePlanning(
  app: RoutePlanningApplicationRef,
  excludeRouteId?: number
): boolean {
  const status = String(app.status ?? '').trim().toUpperCase();
  if (!PLANNING_STATUSES.has(status)) return false;
  if (resolveApplicationActiveRouteId(app, excludeRouteId) != null) return false;
  return true;
}

/** Picker list: only CREATED/IN_WORK, not on another active route. */
export function filterApplicationsForRoutePlanningPicker(
  applications: RoutePlanningApplicationRef[],
  excludeRouteId?: number
): RoutePlanningApplicationRef[] {
  return applications.filter((app) => isApplicationAvailableForRoutePlanning(app, excludeRouteId));
}

/** Include current route's apps for edit hydrate (read-only context), still hidden from picker. */
export function filterApplicationsForRoutePlanningPickerWithEditContext(params: {
  applications: RoutePlanningApplicationRef[];
  editingRouteId?: number;
  linkedApplicationIds?: number[];
}): RoutePlanningApplicationRef[] {
  const linked = new Set(
    (params.linkedApplicationIds ?? []).map(Number).filter(Number.isFinite)
  );
  return params.applications.filter((app) => {
    if (isApplicationAvailableForRoutePlanning(app, params.editingRouteId)) return true;
    if (
      params.editingRouteId != null &&
      linked.has(Number(app.id)) &&
      PLANNING_STATUSES.has(String(app.status ?? '').toUpperCase())
    ) {
      return true;
    }
    return false;
  });
}

export function formatApplicationOccupiedRouteLabel(routeId: number): string {
  return `Уже в маршруте №${routeId}`;
}

function applicationPickerLabel(app: RoutePlanningApplicationRef): string {
  const base = `${app.internalNumber || app.id}${app.client?.name ? ` — ${app.client.name}` : ''}`;
  return base;
}

/** Ant Design Select options — occupied apps hidden; retain current group binding on edit. */
export function buildApplicationPickerSelectOptions(params: {
  applications: RoutePlanningApplicationRef[];
  excludeRouteId?: number;
  retainApplicationId?: number | null;
}): Array<{ value: number; label: string; disabled?: boolean }> {
  const retainId =
    params.retainApplicationId != null && Number.isFinite(Number(params.retainApplicationId))
      ? Number(params.retainApplicationId)
      : null;

  const available = filterApplicationsForRoutePlanningPicker(
    params.applications,
    params.excludeRouteId
  );
  const availableIds = new Set(available.map((a) => a.id));

  const out: Array<{ value: number; label: string; disabled?: boolean }> = [];

  for (const app of params.applications) {
    if (availableIds.has(app.id)) {
      out.push({ value: app.id, label: applicationPickerLabel(app) });
      continue;
    }
    if (retainId === app.id) {
      const occupied = resolveApplicationActiveRouteId(app, params.excludeRouteId);
      const suffix =
        occupied != null ? ` — ${formatApplicationOccupiedRouteLabel(occupied)}` : '';
      out.push({
        value: app.id,
        label: `${applicationPickerLabel(app)}${suffix}`,
        disabled: occupied != null
      });
    }
  }

  return out.sort((a, b) => String(a.label).localeCompare(String(b.label), 'ru'));
}
